import React, { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import styled from 'styled-components';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Header from '../elementos/basicos/Header';
import Footer from '../elementos/basicos/Footer';
import CardCursoArea from '../elementos/basicos/CardCursoArea';
import { AreasContext } from '../../peticiones_API/Peticiones_Areas';
import { getAreaPorSlug, getCursosDeArea } from '../../peticiones_API/Peticiones_Areas';

const CursosArea = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { areas } = useContext(AreasContext);

  const [area, setArea] = useState(null);
  const [cursos, setCursos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let activo = true;
    setLoading(true);
    setError(false);

    Promise.all([getAreaPorSlug(slug), getCursosDeArea(slug)])
      .then(([areaData, cursosData]) => {
        if (!activo) return;
        setArea(areaData);
        setCursos(Array.isArray(cursosData) ? cursosData : []);
      })
      .catch((err) => {
        if (!activo) return;
        if (err?.response?.status === 404) {
          toast.info('El área que buscas ya no está disponible');
          navigate('/areas');
        } else {
          setError(true);
        }
      })
      .finally(() => { if (activo) setLoading(false); });

    return () => { activo = false; };
  }, [slug, navigate]);

  const reintentar = () => {
    setLoading(true);
    setError(false);
    Promise.all([getAreaPorSlug(slug), getCursosDeArea(slug)])
      .then(([a, c]) => { setArea(a); setCursos(Array.isArray(c) ? c : []); })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  };

  if (loading) {
    return (<><Header /><Cargando>Cargando…</Cargando><Footer /></>);
  }

  if (error) {
    return (
      <>
        <Header />
        <Centrado>
          <p>No se pudieron cargar los cursos. Vuelve a intentarlo en unos minutos.</p>
          <BotonReintentar onClick={reintentar}>Reintentar</BotonReintentar>
        </Centrado>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{area?.nombre ? `${area.nombre} | TusCursosSepe` : 'Área | TusCursosSepe'}</title>
        <meta name="description" content={`Cursos gratuitos subvencionados de ${area?.nombre || 'formación'}.`} />
      </Helmet>
      <Header />

      <Banner>
        <BannerInner>
          <h1>{area?.nombre}</h1>
        </BannerInner>
      </Banner>

      {areas && areas.length > 1 && (
        <SelectorWrap>
          <label htmlFor="selector-area">Cambiar de área:</label>
          <Selector
            id="selector-area"
            value={slug}
            onChange={(e) => navigate(`/areas/${e.target.value}`)}
          >
            {areas.map((a) => (
              <option key={a.slug} value={a.slug}>{a.nombre}</option>
            ))}
          </Selector>
        </SelectorWrap>
      )}

      <Seccion>
        <Container>
          {cursos.length === 0 ? (
            <Centrado><p>Aún no hay cursos publicados en esta área.</p></Centrado>
          ) : (
            <Row className="justify-content-center">
              <CardCursoArea cursos={cursos} slug={slug} />
            </Row>
          )}
        </Container>
      </Seccion>

      <Footer />
    </>
  );
};

const Banner = styled.div`
  position: relative;
  width: 100%;
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  background: #FFFFFF;
  border-top: 3px solid #F97316;
  border-bottom: 3px solid #F97316;
`;

const BannerInner = styled.div`
  padding: 3rem 2rem;

  h1 {
    font-family: 'Montserrat', sans-serif;
    font-weight: 1000;
    font-size: 2rem;
    text-transform: uppercase;
    margin: 0;
    color: #111;
  }
`;

const SelectorWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  justify-content: center;
  padding: 1.6rem 1rem 0;
  flex-wrap: wrap;

  label {
    font-family: 'Montserrat', sans-serif;
    font-weight: 600;
    color: #333;
    margin: 0;
  }
`;

const Selector = styled.select`
  width: 360px;
  max-width: 90vw;
  padding: 0.7rem 1rem;
  border: 2px solid #ee805d;
  border-radius: 8px;
  font-family: 'Montserrat', sans-serif;
  font-size: 1rem;
  color: #333;
  background: #fff;
  cursor: pointer;
  outline: none;
`;

const Seccion = styled.div`
  padding: 1% 0 5%;
  min-height: 30vh;
`;

const Cargando = styled.div`
  text-align: center;
  padding: 12vh 1rem;
  font-family: 'Montserrat', sans-serif;
  color: #888;
`;

const Centrado = styled.div`
  text-align: center;
  padding: 6vh 1rem;
  font-family: 'Montserrat', sans-serif;
  color: #666;

  p { font-size: 1.05rem; margin-bottom: 1.2rem; }
`;

const BotonReintentar = styled.button`
  border: none;
  background: #ee805d;
  color: #fff;
  font-weight: 600;
  padding: 0.6rem 1.6rem;
  border-radius: 6px;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

export default CursosArea;
